import React, { useEffect, useMemo } from 'react';
import GeneralViewerInstanceDetail from '../GeneralViewer/GeneralViewerInstanceDetail';
import type { GeneralViewerInstance } from '../../../helpers/api/view_frame/view-frames';

/** Everything the single-frame editor needs to open one exact report frame. */
export interface FrameEditorTarget {
  frame_id: string;
  /** Title shown in the modal header (view name, measurement label, ...). */
  display_name: string;
  view_code?: string | null;
  view_def_id?: number | null;
  sop_instance_uid: string;
  serie_instance_uid: string;
  format_file?: string | null;
  frame_count?: number | null;
  modality?: string | null;
  sop_class_uid?: string | null;
  fps?: number | null;
  width?: number | null;
  height?: number | null;
}

export interface FrameEditorNav {
  total: number;
  /** 0-based index of the currently open item. */
  currentIndex: number;
  onNavigate: (index: number) => void;
}

interface FrameEditorModalProps {
  studyUID: string;
  target: FrameEditorTarget;
  /** Small helper line under the title. */
  subtitle?: string;
  /** Prev/next cycles through sibling items (captured views, measurements, ...). */
  nav?: FrameEditorNav;
  onClose: () => void;
}

/**
 * Report frame-explainability modal: opens exactly one frame in the single-frame
 * segmentation editor (authoritative image + the view's own segmentations), on top
 * of the report page. Closes on Escape or the close button.
 */
export function FrameEditorModal({
  studyUID,
  target,
  subtitle,
  nav,
  onClose,
}: FrameEditorModalProps) {
  const editorInstance = useMemo<GeneralViewerInstance>(
    () => ({
      sop_instance_uid: target.sop_instance_uid,
      serie_instance_uid: target.serie_instance_uid,
      frame_count: target.frame_count ?? 0,
      first_frame_image_url: null,
      format_file:
        target.format_file === 'VIDEO' || target.format_file === 'DICOM'
          ? target.format_file
          : 'DICOM',
      modality: target.modality,
      sop_class_uid: target.sop_class_uid,
      fps: target.fps,
      width: target.width,
      height: target.height,
    }),
    [target]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/80 backdrop-blur-sm p-3 sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit ${target.display_name}`}
    >
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl bg-white dark:bg-gray-950 shadow-2xl border border-white/10">
        <div className="h-12 shrink-0 flex items-center justify-between gap-3 px-4 border-b border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-800 dark:text-gray-100">
              {target.display_name}
              {nav && nav.total > 1 && (
                <span className="ml-2 text-[10px] font-medium text-slate-400 dark:text-gray-500">
                  {nav.currentIndex + 1} of {nav.total}
                </span>
              )}
            </p>
            {subtitle && (
              <p className="text-[10px] text-slate-400 dark:text-gray-500">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-500 dark:text-gray-400 text-xl transition-colors"
            aria-label="Close editor"
          >
            ×
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <GeneralViewerInstanceDetail
            key={target.frame_id}
            studyUID={studyUID}
            instance={editorInstance}
            onBack={onClose}
            initialFrameId={target.frame_id}
            restrictToFrameId={target.frame_id}
            restrictToViewDefId={target.view_def_id ?? null}
            segmentationViewCode={target.view_code ?? null}
            externalNav={nav}
            readOnly
          />
        </div>
      </div>
    </div>
  );
}

export default FrameEditorModal;
